import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, getAllPatients, getAllUsers, getAllActivityLogs } from '../firebase';
import { User } from '../types';
import { isSuperAdmin, canViewPatient } from '../utils/roleUtils';

interface DashboardStats {
  totalCases: number;
  totalPatients: number;
  totalUsers: number;
  totalEvents: number;
  totalActivities: number;
  activeCases: number;
  archivedCases: number;
  inactiveCases: number;
  treatmentCompletionCount: number; // Count of patients who completed full process
  recentlyUpdatedPatients: number;
  upcomingMeetings: number;
}

export function useDashboardStats(
  user: User | null,
  events: any[],
  loadAllMilestones: () => Promise<any[]>,
  setActivityLogs: (logs: any[]) => void
) {
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalCases: 0,
    totalPatients: 0,
    totalUsers: 0,
    totalEvents: 0,
    totalActivities: 0,
    activeCases: 0,
    archivedCases: 0,
    inactiveCases: 0,
    treatmentCompletionCount: 0,
    recentlyUpdatedPatients: 0,
    upcomingMeetings: 0
  });
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [inactivePatients, setInactivePatients] = useState<any[]>([]);

  useEffect(() => {
    const loadDashboardStats = async () => {
      if (!user) return;
      
      setIsStatsLoading(true);
      try {
        const [patientsData, usersData, activityData] = await Promise.all([
          getAllPatients(),
          getAllUsers(),
          getAllActivityLogs()
        ]);
        
        // Load user's programs and groups for permission checking
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        const allPrograms = programsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Array<{ id: string; organizationId?: string; managers?: string[] }>;
        
        const userPrograms = allPrograms
          .filter(program => {
            return Array.isArray(program.managers) && program.managers.includes(user.id);
          })
          .map(program => program.id);
        
        const groupsSnapshot = await getDocs(collection(db, 'groups'));
        const userGroups = groupsSnapshot.docs
          .filter(doc => {
            const data = doc.data();
            return Array.isArray(data.managers) && data.managers.includes(user.id);
          })
          .map(doc => doc.id);
        
        // Apply role-based filtering using proper permission system
        let filteredPatients = patientsData;
        let filteredEvents = events;
        let filteredActivities = activityData;
        
        if (!isSuperAdmin(user)) {
          // Use canViewPatient for proper role-based filtering
          filteredPatients = patientsData.filter(p => canViewPatient(user, p, userPrograms, userGroups));
          
          // Events: non-super-admins see only their own
          filteredEvents = events.filter(e => e.createdBy === user.id);
          
          // Activities: Filter based on viewable patients
          // Create a set of viewable patient IDs for efficient lookup
          const viewablePatientIds = new Set(filteredPatients.map(p => p.caseId));
          
          filteredActivities = activityData.filter(a => {
            // Include activities for patients the user can view
            if (a.caseId && viewablePatientIds.has(a.caseId)) {
              return true;
            }
            // Also include activities without caseId that were created by the user
            // (for backward compatibility with legacy activities)
            if (!a.caseId && a.createdBy === user.id) {
              return true;
            }
            return false;
          });
        }
        
        // Calculate inactive cases (cases not updated for more than a month)
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        const inactivePatientsList = filteredPatients.filter(patient => {
          if (!patient.updatedAt) return true; // If no update date, consider inactive
          const updatedDate = patient.updatedAt.toDate ? patient.updatedAt.toDate() : new Date(patient.updatedAt);
          return updatedDate < oneMonthAgo;
        });
        
        setInactivePatients(inactivePatientsList);
        const inactiveCases = inactivePatientsList.length;
        
        // Count archived cases (patients with status 'inactive' or 'completed')
        // These are considered "archived" or "completed process"
        const completedProcessCount = filteredPatients.filter(patient => {
          const status = patient.status?.toLowerCase();
          return status === 'inactive' || status === 'completed';
        }).length;
        
        // Calculate recently updated patients (updated in last 7 days by current user)
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        
        const recentlyUpdatedPatients = filteredPatients.filter(patient => {
          if (!patient.updatedAt) return false;
          const updatedDate = patient.updatedAt.toDate ? patient.updatedAt.toDate() : new Date(patient.updatedAt);
          return updatedDate >= oneWeekAgo;
        }).length;
        
        // Calculate upcoming meetings (events scheduled for today or future)
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Start of today
        
        const upcomingMeetings = filteredEvents.filter(event => {
          if (!event.date) return false;
          const eventDate = event.date.toDate ? event.date.toDate() : new Date(event.date);
          return eventDate >= today;
        }).length;
        
        // Calculate active vs archived cases
        const totalCases = filteredPatients.length;
        // Archived cases are patients with status 'inactive' or 'completed'
        const archivedCases = completedProcessCount;
        // Active cases are all other cases (status 'new' or 'active')
        const activeCases = totalCases - archivedCases;

        setDashboardStats({
          totalCases: filteredPatients.length,
          totalPatients: filteredPatients.length,
          totalUsers: isSuperAdmin(user) ? usersData.length : 1, // Regular users only see themselves
          totalEvents: filteredEvents.length,
          totalActivities: filteredActivities.length,
          activeCases: activeCases,
          archivedCases: archivedCases,
          inactiveCases: inactiveCases,
          treatmentCompletionCount: completedProcessCount,
          recentlyUpdatedPatients: recentlyUpdatedPatients,
          upcomingMeetings: upcomingMeetings
        });
        
        setActivityLogs(filteredActivities);
      } catch (error) {
        console.error('Error loading dashboard stats:', error);
      } finally {
        setIsStatsLoading(false);
      }
    };

    loadDashboardStats();
  }, [user, events, loadAllMilestones, setActivityLogs]);

  return {
    dashboardStats,
    isStatsLoading,
    inactivePatients
  };
}


